import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import OpenEye from '../../static/images/signup/Openeye.svg';
import CloseEye from '../../static/images/signup/closeeye.svg';
import { IMAGE_CONSTANT, LOCAL_STORAGE_CONSTANT } from '../../constants/app';
import Button from '../../ui-core/Button/Button';
import { APP_ROUTES, getRoute } from '../../route';
import {
  initiateEmailChangeRequest,
  requestEmailChangeRequest,
  resendConfirmationEmailChangeRequest,
  resendCurrentEmailChangeRequest,
  verifyCurrentEmailChangeRequest,
} from '../../api/community';
import { doGetUserDataRequest } from '../../store/action/user';
import { getUserRequest } from '../../api/user';
import {
  createNotification,
  NOTIFICATION_TYPE,
} from '../../services/Notification';
import { errorHandler } from '../../utils/helpers';
import { extractJSON } from '../../services/jsonUtil';

import '../NewHome/components/OtpModal/style.css';
import './style.css';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const STEPS = {
  VERIFY: 'verify',
  REQUEST: 'request',
  CONFIRM: 'confirm',
};

const requestValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Please enter a new email.'),
  password: Yup.string().required('Please enter your password.'),
});

const getErrorMessage = error => {
  const handled = errorHandler(error);
  return (
    error?.response?.data?.message ||
    (typeof handled?.message === 'string' ? handled.message : null) ||
    'Something went wrong'
  );
};

const getAuthorizationToken = response =>
  response?.data?.authorization_token ||
  response?.data?.data?.authorization_token;

const ChangeEmail = ({ history, user }) => {
  const dispatch = useDispatch();
  const inputRef = useRef([]);
  const [step, setStep] = useState(STEPS.VERIFY);
  const [otp, setOtp] = useState(() => new Array(OTP_LENGTH).fill(''));
  const [counter, setCounter] = useState(RESEND_SECONDS);
  const [otpError, setOtpError] = useState(false);
  const [authorizationToken, setAuthorizationToken] = useState('');
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const currentEmail = user?.attributes?.email || '';

  const handleBack = () => {
    const url = getRoute(APP_ROUTES.PROFILE_WITH_SUB_ROUTES, {
      pathParam: { section: 'edit-profile' },
    });
    history?.push(url);
  };

  const sendVerificationCode = async () => {
    await initiateEmailChangeRequest();
    setCounter(RESEND_SECONDS);
    setOtp(new Array(OTP_LENGTH).fill(''));
    setOtpError(false);
    inputRef.current[0]?.focus();
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setIsInitLoading(true);
      try {
        await sendVerificationCode();
      } catch (error) {
        if (!cancelled) {
          createNotification({
            type: NOTIFICATION_TYPE.ERROR,
            message: getErrorMessage(error),
          });
        }
      } finally {
        if (!cancelled) {
          setIsInitLoading(false);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (counter <= 0) return undefined;

    const timeout = setTimeout(() => {
      setCounter(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [counter]);

  useEffect(() => {
    if (step === STEPS.VERIFY && !isInitLoading) {
      inputRef.current[0]?.focus();
    }
  }, [step, isInitLoading]);

  const applyOtpDigits = (digits, startIndex = 0) => {
    const nextOtp = [...otp];

    digits.forEach((digit, offset) => {
      const fieldIndex = startIndex + offset;
      if (fieldIndex < OTP_LENGTH) {
        nextOtp[fieldIndex] = digit;
      }
    });

    setOtp(nextOtp);
    setOtpError(false);

    nextOtp.forEach((digit, fieldIndex) => {
      const input = inputRef.current[fieldIndex];
      if (!input) return;
      input.classList.toggle('filled', Boolean(digit));
      input.classList.remove('error');
    });

    const lastFilledIndex = Math.min(
      startIndex + digits.length - 1,
      OTP_LENGTH - 1,
    );
    inputRef.current[lastFilledIndex]?.focus();
  };

  const handleOtpChange = (element, index) => {
    const raw = element?.value ?? '';
    const digits = raw.replace(/\D/g, '').split('');

    if (digits.length > 1) {
      applyOtpDigits(digits, index);
      return true;
    }

    if (raw && /\D/.test(raw)) return false;

    const nextOtp = otp.map((digit, idx) =>
      idx === index ? digits[0] || '' : digit,
    );

    if (digits[0]) {
      element.classList.add('filled');
    } else {
      element.classList.remove('filled');
    }

    setOtp(nextOtp);
    setOtpError(false);

    if (digits[0]) {
      inputRef.current[index + 1]?.focus();
    }

    return true;
  };

  const handleOtpPaste = (event, index) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH - index);

    if (!pasted) return;

    applyOtpDigits(pasted.split(''), index);
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.keyCode === 8 && !otp[index]) {
      inputRef.current[index - 1]?.focus();
      inputRef.current[index - 1]?.select();
    } else if (event.keyCode === 13 && otp.every(Boolean)) {
      handleVerifySubmit();
    }
  };

  const handleResendCode = async event => {
    event.preventDefault();
    if (counter > 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await resendCurrentEmailChangeRequest();
      setCounter(RESEND_SECONDS);
      setOtp(new Array(OTP_LENGTH).fill(''));
      setOtpError(false);
      inputRef.current[0]?.focus();
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Verification code sent successfully.',
      });
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendConfirmation = async event => {
    event.preventDefault();
    if (counter > 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await resendConfirmationEmailChangeRequest();
      setCounter(RESEND_SECONDS);
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Confirmation email resent successfully.',
      });
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationCheck = async () => {
    if (isSubmitting) return;

    const storedUser = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
    if (!storedUser?.id) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: 'Unable to check your email verification status.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await getUserRequest(storedUser.id);
      const refreshedUser = response?.data?.response?.data;
      const verifiedEmail = refreshedUser?.attributes?.email;
      const emailWasChanged =
        verifiedEmail?.trim().toLowerCase() === newEmail.trim().toLowerCase();

      if (!emailWasChanged) {
        createNotification({
          type: NOTIFICATION_TYPE.ERROR,
          message:
            'Your new email is not verified yet. Open the confirmation link, then try again.',
        });
        return;
      }

      dispatch(doGetUserDataRequest({ userId: storedUser.id }));
      createNotification({
        type: NOTIFICATION_TYPE.SUCCESS,
        message: 'Email verified successfully.',
      });
      handleBack();
    } catch (error) {
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async () => {
    if (!otp.every(Boolean) || isSubmitting) return;

    setIsSubmitting(true);
    setOtpError(false);

    try {
      const response = await verifyCurrentEmailChangeRequest({
        code: otp.join(''),
      });
      const token = getAuthorizationToken(response);

      if (!token) {
        throw new Error('Unable to verify email change. Please try again.');
      }

      setAuthorizationToken(token);
      setStep(STEPS.REQUEST);
    } catch (error) {
      setOtpError(true);
      otp.forEach((_, index) => {
        inputRef.current[index]?.classList.add('error');
      });
      createNotification({
        type: NOTIFICATION_TYPE.ERROR,
        message: getErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    validationSchema: requestValidationSchema,
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: async values => {
      if (!authorizationToken) {
        createNotification({
          type: NOTIFICATION_TYPE.ERROR,
          message: 'Please verify your current email first.',
        });
        setStep(STEPS.VERIFY);
        return;
      }

      setIsSubmitting(true);

      try {
        await requestEmailChangeRequest({
          authorization_token: authorizationToken,
          password: values.password,
          email: values.email,
        });

        const storedUser = extractJSON(LOCAL_STORAGE_CONSTANT.USER);
        if (storedUser?.id) {
          dispatch(doGetUserDataRequest({ userId: storedUser.id }));
        }

        setNewEmail(values.email);
        setCounter(RESEND_SECONDS);
        setStep(STEPS.CONFIRM);
      } catch (error) {
        createNotification({
          type: NOTIFICATION_TYPE.ERROR,
          message: getErrorMessage(error),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const isVerifySubmitDisabled =
    isInitLoading || isSubmitting || !otp.every(Boolean);

  const isRequestSubmitDisabled =
    isSubmitting || !formik.isValid || !formik.dirty;

  const renderOtpStep = () => (
    <>
      <p className="change-email-subtitle">
        Enter the verification code sent to your email{' '}
        <strong>{currentEmail}</strong>
      </p>

      <div className="otp-field-wrap change-email-otp-wrap">
        <div className="d-flex justify-content-center">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={el => {
                inputRef.current[index] = el;
              }}
              className={`otp-field mx-1${otpError ? ' error' : ''}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={isInitLoading || isSubmitting}
              onChange={e => handleOtpChange(e.target, index)}
              onPaste={e => handleOtpPaste(e, index)}
              onKeyDown={e => handleOtpKeyDown(e, index)}
              onFocus={e => e.target.select()}
            />
          ))}
        </div>
      </div>

      <div className="change-email-resend counter">
        {counter > 0 ? (
          <p>
            Resend code in <span>00:{String(counter).padStart(2, '0')}</span>
          </p>
        ) : (
          <button
            type="button"
            className="change-email-resend-btn"
            onClick={handleResendCode}
            disabled={isSubmitting}
          >
            Resend code
          </button>
        )}
      </div>

      <Button
        class="change-password-submit-btn"
        text="SUBMIT"
        type="button"
        onClick={handleVerifySubmit}
        isDisabled={isVerifySubmitDisabled}
        isLoading={isSubmitting}
      />
    </>
  );

  const renderRequestStep = () => (
    <form className="change-password-form" onSubmit={formik.handleSubmit}>
      <div className="change-password-field">
        <label className="account-settings-label" htmlFor="new-email">
          NEW EMAIL
        </label>
        <div className="change-password-input-row">
          <input
            id="new-email"
            type="email"
            name="email"
            className="change-password-input"
            placeholder="Enter new email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        {formik.touched.email && formik.errors.email && (
          <span className="account-settings-error">{formik.errors.email}</span>
        )}
      </div>

      <div className="change-password-field">
        <label className="account-settings-label" htmlFor="email-password">
          PASSWORD
        </label>
        <div className="change-password-input-row">
          <input
            id="email-password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            className="change-password-input"
            placeholder="Enter password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <button
            type="button"
            className="change-password-toggle"
            onClick={() => setShowPassword(prev => !prev)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <img src={showPassword ? OpenEye : CloseEye} alt="" />
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <span className="account-settings-error">
            {formik.errors.password}
          </span>
        )}
      </div>

      <p className="change-password-hint">
        A confirmation will be sent to this account. Click on the confirmation
        link to verify and add this email.
      </p>

      <Button
        class="change-password-submit-btn"
        text="SUBMIT"
        type="submit"
        isDisabled={isRequestSubmitDisabled}
        isLoading={isSubmitting}
      />
    </form>
  );

  const renderConfirmStep = () => (
    <>
      <p className="change-email-subtitle">
        Confirm your email by clicking the link we sent you at{' '}
        <strong>{newEmail}</strong> to activate your email address.
      </p>
      <div className="email-confirm-actions">
        <Button
          class="change-password-submit-btn change-email-confirm-verify-btn"
          text="VERIFY"
          type="button"
          onClick={handleConfirmationCheck}
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
        />
        <a href="mailto:" className="email-confirm-outline-btn">
          OPEN EMAIL
        </a>
        <div className="email-confirm-resend-wrap">
          {counter > 0 ? (
            <p className="email-confirm-resend-timer">
              Resend in <span>00:{String(counter).padStart(2, '0')}</span>
            </p>
          ) : (
            <button
              type="button"
              className="change-email-resend-btn"
              onClick={handleResendConfirmation}
              disabled={isSubmitting}
            >
              Resend email
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="change-password-page change-email-page">
      <div className="change-password-container">
        <button
          type="button"
          className="change-password-back"
          onClick={handleBack}
        >
          <img
            src={IMAGE_CONSTANT.backIcon}
            alt=""
            className="change-password-back-icon"
          />
          BACK
        </button>

        <h1 className="change-password-title">Change email</h1>

        {step === STEPS.VERIFY
          ? renderOtpStep()
          : step === STEPS.REQUEST
          ? renderRequestStep()
          : renderConfirmStep()}
      </div>
    </div>
  );
};

ChangeEmail.propTypes = {
  history: PropTypes.object,
  user: PropTypes.object,
};

export default ChangeEmail;
